import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { CheckCircle2, AlertCircle, Mail, RefreshCw, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  magic_link: "Portal invite",
  invoice: "Invoice",
  esign: "eSign",
  file_request: "File request",
};

function fmt(iso) { if (!iso) return ""; return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }

export default function EmailLog() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ kind: "", status: "", to: "" });
  const [drawer, setDrawer] = useState(null);

  const reload = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter.kind) params.append("kind", filter.kind);
    if (filter.status) params.append("status", filter.status);
    if (filter.to) params.append("to", filter.to);
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API_BASE}/email-log?${params}`),
        axios.get(`${API_BASE}/email-log/summary`),
      ]);
      setRows(r.data); setSummary(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, [filter.kind, filter.status]);  // eslint-disable-line react-hooks/exhaustive-deps

  const [digestSending, setDigestSending] = useState(false);

  const runDigests = async () => {
    setDigestSending(true);
    try {
      const r = await axios.post(`${API_BASE}/digests/run-now`);
      toast.success(`Sent ${r.data.sent} digests · ${r.data.skipped} clients with nothing pending skipped`);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDigestSending(false); }
  };

  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="email-log-page">
      <div className="overline mb-2">Audit</div>
      <h1 className="font-heading text-4xl mb-2" style={{ fontWeight: 500 }}>
        Email <em className="italic" style={{ color: "var(--accent)" }}>log</em>
      </h1>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <p className="text-[var(--ink-2)] max-w-2xl">
          Every outbound email — magic-link invites, invoice receipts, eSign requests, file-request notifications.
          Weekly client digests auto-send Monday 08:00 UTC.
        </p>
        <button
          onClick={async () => {
            try {
              const r = await axios.post(`${API_BASE}/digests/run-now`);
              toast.success(`Sent ${r.data.sent} digests · ${r.data.skipped} clients with nothing pending skipped`);
              await reload();
            } catch (e) { toast.error(formatApiError(e)); }
          }}
          className="btn-secondary"
          data-testid="digest-run-now-btn"
        >
          <Send size={12} /> Send weekly digests now
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="email-log-summary">
          <Stat label="Total sent" value={summary.total} />
          <Stat label="Delivered" value={summary.ok} accent="var(--moss)" />
          <Stat label="Failed" value={summary.failed} accent="var(--accent)" />
          <Stat label="Kinds" value={Object.keys(summary.by_kind || {}).length} />
        </div>
      )}

      <div className="card p-4 mb-5 flex flex-wrap items-center gap-3" data-testid="email-log-filters">
        <select value={filter.kind} onChange={(e) => setFilter({ ...filter, kind: e.target.value })}
          className="bg-white border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px]" data-testid="email-log-filter-kind">
          <option value="">All kinds</option>
          {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="bg-white border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px]" data-testid="email-log-filter-status">
          <option value="">All statuses</option>
          <option value="ok">Delivered</option>
          <option value="failed">Failed</option>
        </select>
        <input
          value={filter.to}
          placeholder="Search recipient"
          onChange={(e) => setFilter({ ...filter, to: e.target.value })}
          onKeyDown={(e) => { if (e.key === "Enter") reload(); }}
          className="flex-1 min-w-[200px] bg-white border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px]"
          data-testid="email-log-filter-to"
        />
        <button onClick={reload} className="btn-secondary" data-testid="email-log-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loading ? <div className="text-[var(--ink-3)]">Loading…</div>
        : rows.length === 0 ? (
        <div className="card p-10 text-center" data-testid="email-log-empty">
          <Mail size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-heading text-xl mb-1" style={{ fontWeight: 500 }}>No emails match</div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="report-table">
            <thead><tr><th>When</th><th>Kind</th><th>To</th><th>Subject</th><th>Status</th><th>Resend ID</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.log_id} data-testid={`email-log-row-${r.log_id}`}
                    onClick={() => setDrawer(r)}
                    className="cursor-pointer hover:bg-[var(--subtle)]">
                  <td className="text-[12px] text-[var(--ink-3)] font-mono">{fmt(r.created_at)}</td>
                  <td><span className="badge badge-outline">{KIND_LABEL[r.kind] || r.kind}</span></td>
                  <td className="text-[12px] font-mono">{r.to}</td>
                  <td className="text-[13px] max-w-[280px] truncate" title={r.subject}>{r.subject}</td>
                  <td>
                    {r.result?.ok ? (
                      <span className="inline-flex items-center gap-1 text-[12px]" style={{ color: "var(--moss)" }}>
                        <CheckCircle2 size={12} /> delivered
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[12px]" style={{ color: "var(--accent)" }}
                        title={r.result?.error || ""}>
                        <AlertCircle size={12} /> {r.result?.error ? "failed" : "skipped"}
                      </span>
                    )}
                  </td>
                  <td className="text-[11px] font-mono text-[var(--ink-3)] truncate max-w-[160px]" title={r.result?.id || ""}>
                    {r.result?.id || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {drawer && <EmailLogDrawer log={drawer} onClose={() => setDrawer(null)} onRetried={reload} />}
    </div>
  );
}

function EmailLogDrawer({ log, onClose, onRetried }) {
  const [retrying, setRetrying] = useState(false);

  const retry = async () => {
    setRetrying(true);
    try {
      const r = await axios.post(`${API_BASE}/email-log/${log.log_id}/retry`);
      toast.success(r.data?.ok ? "Resend succeeded" : "Resent — check the result");
      await onRetried();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRetrying(false);
    }
  };

  const failed = !log.result?.ok;
  const cannotRetry = !log.html;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(26,24,20,0.45)" }}
         onClick={onClose} data-testid="email-log-drawer">
      <div className="bg-[var(--paper)] border-l border-[var(--border)] max-w-xl w-full h-full overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="px-7 py-6 border-b border-[var(--border)] flex items-start justify-between gap-4 sticky top-0 bg-[var(--paper)] z-10">
          <div>
            <div className="overline mb-1">Delivery details</div>
            <h3 className="font-heading text-xl truncate" style={{ fontWeight: 500 }} data-testid="email-log-drawer-subject">{log.subject}</h3>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="email-log-drawer-close">
            <X size={20} />
          </button>
        </div>

        <div className="px-7 py-5 space-y-5">
          <DetailRow label="To" value={log.to} mono />
          <DetailRow label="Kind" value={KIND_LABEL[log.kind] || log.kind} />
          <DetailRow label="When" value={new Date(log.created_at).toLocaleString()} mono />
          <DetailRow
            label="Status"
            value={
              <span className="inline-flex items-center gap-1" style={{ color: failed ? "var(--accent)" : "var(--moss)" }}>
                {failed ? <AlertCircle size={14} /> : <CheckCircle2 size={14} />}
                {failed ? "Failed" : "Delivered"}
              </span>
            }
            testid="email-log-drawer-status"
          />
          {log.result?.id && <DetailRow label="Resend ID" value={log.result.id} mono />}
          {log.result?.error && (
            <DetailRow
              label="Error"
              value={<span style={{ color: "var(--accent)" }}>{log.result.error}</span>}
              testid="email-log-drawer-error"
            />
          )}
          {log.ref && Object.keys(log.ref).length > 0 && (
            <div>
              <div className="overline mb-2">Reference</div>
              <pre className="bg-[var(--subtle)] border border-[var(--border)] rounded-md p-3 text-[11px] font-mono overflow-x-auto"
                   data-testid="email-log-drawer-ref">
                {JSON.stringify(log.ref, null, 2)}
              </pre>
            </div>
          )}
          {log.html && (
            <div>
              <div className="overline mb-2">Rendered email</div>
              <iframe
                title="Email preview"
                srcDoc={log.html}
                data-testid="email-log-drawer-preview"
                className="w-full bg-white border border-[var(--border)] rounded-md"
                style={{ height: 380 }}
              />
            </div>
          )}
        </div>

        <div className="px-7 py-5 border-t border-[var(--border)] sticky bottom-0 bg-[var(--paper)] flex items-center justify-end gap-3">
          {cannotRetry ? (
            <div className="text-[11px] text-[var(--ink-3)] mr-auto" data-testid="email-log-drawer-no-retry">
              Pre-retry-feature log — cannot replay.
            </div>
          ) : (
            <div className="text-[11px] text-[var(--ink-3)] mr-auto">
              Retrying re-sends the exact same email body to the same recipient.
            </div>
          )}
          <button onClick={onClose} className="btn-secondary">Close</button>
          <button onClick={retry} disabled={cannotRetry || retrying} className="btn-primary"
                  data-testid="email-log-drawer-retry">
            {retrying ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {retrying ? "Sending…" : (failed ? "Retry send" : "Send again")}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, mono, testid }) {
  return (
    <div data-testid={testid}>
      <div className="overline text-[10px] mb-1">{label}</div>
      <div className={`text-[13px] ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="card p-4" data-testid={`email-log-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="overline mb-1">{label}</div>
      <div className="font-heading text-3xl tabular" style={{ fontWeight: 500, color: accent || "var(--ink)" }}>{value}</div>
    </div>
  );
}
